/**
 * JWT token management for the Virtuosa Symfony API.
 * Stores the token in localStorage and provides helpers.
 */

const TOKEN_KEY = "virtuosa:jwt-token";
const USER_KEY = "virtuosa:user";

export interface VirtuosaUser {
  id: string;
  email: string;
  /**
   * Le nom choisi, `null` tant qu'il ne l'est pas.
   *
   * Le seul nom que le produit montre à quelqu'un d'autre. Sur son propre
   * écran, `null` se replie sur l'adresse ; chez les autres, sur rien.
   */
  nickname: string | null;
  roles: string[];
  /** Le forfait qui s'applique ; `assigned: false`, celui par défaut. */
  plan: { id: string; name: string; assigned: boolean } | null;
  /** Où en est le compte sur chaque limite de son forfait. */
  limits: AccountLimit[];
  /**
   * Les versions des documents acceptés, et quand. `null` sur un compte créé
   * avant que l'acceptation soit tracée : rien ne lui a été montré.
   *
   * Facultatifs : un utilisateur gardé en `localStorage` par une version
   * antérieure du front ne les a pas, jusqu'au prochain `/me`.
   */
  acceptedTerms?: AcceptedDocument | null;
  acceptedPrivacy?: AcceptedDocument | null;
  legalAcceptedAt?: string | null;
  createdAt: string;
}

/** Une version de document juridique, réduite à ce qui l'identifie. */
export interface AcceptedDocument {
  version: string;
  publishedAt: string | null;
}

/**
 * Une limite du forfait, vue du compte. `amount` et `remaining` à `null` :
 * illimité. Les octets et les jours sont bruts, l'écran les convertit.
 */
export interface AccountLimit {
  key:
    | "exercise_generations"
    | "score_imports"
    | "recording_storage_bytes"
    | "recording_retention_days"
    | "recording_duration_minutes";
  kind: "counter" | "capacity" | "retention" | "ceiling";
  unit: "count" | "bytes" | "days" | "minutes";
  amount: number | null;
  period: "day" | "week" | "month" | "total" | null;
  overridden: boolean;
  used: number;
  remaining: number | null;
}

export interface AuthResponse {
  token: string;
  user: VirtuosaUser;
}

export function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export function getStoredUser(): VirtuosaUser | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setStoredUser(user: VirtuosaUser): void {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}
